import { useState } from "react";
import { getInitials } from "@/components/ui/avatar";
import { cn } from "@/lib/cn";

const PRO_AVATAR_PALETTE = [
  { bg: "bg-lime", text: "text-[#1A1A1E]" },
  { bg: "bg-[#2DD4BF]", text: "text-[#042F2E]" },
  { bg: "bg-[#A78BFA]", text: "text-[#1E1033]" },
  { bg: "bg-[#FB923C]", text: "text-[#431407]" },
  { bg: "bg-[#F472B6]", text: "text-[#500724]" },
] as const;

function getProAvatarColor(seed: string): { bg: string; text: string } {
  let hash = 0;
  for (const char of seed) {
    hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0;
  }
  const index = Math.abs(hash) % PRO_AVATAR_PALETTE.length;
  return PRO_AVATAR_PALETTE[index] ?? PRO_AVATAR_PALETTE[0];
}

interface ProAvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  seed?: string;
  size?: number;
  className?: string;
  imgClassName?: string;
}

function ProAvatar({
  src,
  alt,
  name = "",
  seed,
  size = 36,
  className,
  imgClassName,
}: ProAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;
  const initials = getInitials(name);
  const colorSeed = seed ?? name;
  const { bg, text } = getProAvatarColor(colorSeed);
  const fontSize = Math.max(10, Math.round(size * 0.36));

  return (
    <span
      className={cn(
        "avatar inline-flex shrink-0 items-center justify-center rounded-full font-bold",
        !showImage && bg,
        !showImage && text,
        className,
      )}
      style={{ width: size, height: size }}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name}
          className={cn("size-full rounded-full object-cover object-top", imgClassName)}
          onError={() => setImgError(true)}
        />
      ) : (
        <span style={{ fontSize }} aria-hidden={!!alt}>
          {initials}
        </span>
      )}
    </span>
  );
}

export { getProAvatarColor, ProAvatar, type ProAvatarProps };
